<script setup>
import {useAuthStore} from "~/stores/auth.ts";
import {useAuth} from "~/composables/auth/useAuth.js";

defineProps({
  isOpen: Boolean,
  default: false
})
const authStore = useAuthStore();
const {logout} = useAuth()
const route = useRoute();
const emit = defineEmits(["close-nav", "open-profile-modal"]);


const setHeightToScreen = ref(false);
if (window.innerHeight < 820){
  setHeightToScreen.value = true;
}


watch(() => route.fullPath, () => {
  emit('close-nav')
})
</script>

<template>
  <aside :class="[isOpen ? 'right-0' : '-right-65 sm:-right-70' , {'lg:!h-[calc(100vh-64px)]' : setHeightToScreen}]" class="fixed top-0 bottom-0 z-50 md:static lg:sticky lg:top-5 lg:h-max overflow-y-auto w-65 sm:w-70 md:w-66 lg:w-70 shrink-0 bg-white md:border-l md:border-l-black-10 px-7 py-5 lg:rounded-lg max-md:transition-all max-md:duration-300 lg:dark:darker-gradient" >
    <div class="flex items-center justify-between pb-5 mb-5 border-b border-b-light">
      <div class="flex items-center gap-x-2">
        <img class="size-11 rounded-full" :src="authStore.user?.avatar.url" loading="lazy">
        <div class="flex flex-col text-label">
          <span class="font-demibold max-w-28 truncate select-none">{{ authStore.user?.first_name }}</span>
          <span class="font-regular text-secondary" data-clarity-mask="true">{{ authStore.user?.mobile }}</span>
        </div>
      </div>
      <div class="flex items-center gap-x-3">
        <button class="flex items-center justify-center cursor-pointer" @click="$emit('open-profile-modal'); $emit('close-nav')">
          <Icon name="linear:setting-2" class="size-4.5 text-muted hover:text-primary transition-colors"/>
        </button>
        <button class="flex items-center justify-center cursor-pointer" @click="logout">
          <Icon name="linear:logout" class="size-4.5 text-muted hover:text-red-90 transition-colors"/>
        </button>
      </div>
    </div>
    <div class="flex flex-col gap-y-5">
      <div class="flex flex-col gap-y-2.5">
        <span class="text-label text-secondary font-regular select-none">دسترسی سریع</span>
        <div class="flex flex-col gap-y-2">
          <NuxtLink to="/" class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:home" class="size-5 text-secondary group-hover:text-brand transition-colors" />
            <span class="text-primary group-hover:text-brand text-caption transition-colors">خانه</span>
          </NuxtLink>
          <NuxtLink to="/courses" class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:flash" class="size-5 text-secondary group-hover:text-brand transition-colors" />
            <span class="text-primary group-hover:text-brand text-caption transition-colors">دوره های من</span>
          </NuxtLink>
          <NuxtLink to="/transactions" class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:wallet" class="size-5 text-secondary group-hover:text-brand transition-colors" />
            <span class="text-primary group-hover:text-brand text-caption transition-colors">اشتراک و تراکنش‌ها</span>
          </NuxtLink>
          <NuxtLink to="/tickets" :class="{ 'router-link-active': $route.path.startsWith('/tickets')}" class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:sms" class="size-5 text-secondary group-hover:text-brand transition-colors" />
            <span class="text-primary group-hover:text-brand text-caption transition-colors">تیکت ها</span>
          </NuxtLink>
          <NuxtLink to="/questions" class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:message-question" class="size-5 text-secondary group-hover:text-brand transition-colors" />
            <span class="text-primary group-hover:text-brand text-caption transition-colors">پرسش و پاسخ ها</span>
          </NuxtLink>
<!--          <NuxtLink to="/sessions" class="flex items-center gap-x-2.5 py-1.5 group">-->
<!--            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>-->
<!--            <Icon name="linear:menu-board" class="size-5 text-secondary group-hover:text-brand transition-colors" />-->
<!--            <span class="text-primary group-hover:text-brand text-caption transition-colors">مشاوره های من</span>-->
<!--          </NuxtLink>-->
        </div>
      </div>
      <div class="flex flex-col gap-y-2.5">
        <span class="text-label text-secondary font-regular select-none">سبزلـرن پرمیوم - بزودی</span>
        <div class="flex flex-col gap-y-2">
          <button class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:box" class="size-5 text-secondary group-hover:text-brand transition-colors"/>
            <span class="text-primary group-hover:text-brand text-caption transition-colors">دوره های ویژه</span>
          </button>
          <button class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:youtube" class="size-5 text-secondary group-hover:text-brand transition-colors"/>
            <span class="text-primary group-hover:text-brand text-caption transition-colors">وبینار های من</span>
          </button>
          <button class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:lamp-charge" class="size-5 text-secondary group-hover:text-brand transition-colors"/>
            <span class="text-primary group-hover:text-brand text-caption transition-colors">سبزبات (Ai)</span>
          </button>
          <button class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:microphone-2" class="size-5 text-secondary group-hover:text-brand transition-colors"/>
            <span class="text-primary group-hover:text-brand text-caption transition-colors">پادکست ها</span>
          </button>
          <button class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:folder-open" class="size-5 text-secondary group-hover:text-brand transition-colors"/>
            <span class="text-primary group-hover:text-brand text-caption transition-colors">برنامه تمرینی</span>
          </button>
          <button class="flex items-center gap-x-2.5 py-1.5 group">
            <span class="active-badge block w-0.5 h-5 ml-1 rounded-full"></span>
            <Icon name="linear:24-support" class="size-5 text-secondary group-hover:text-brand transition-colors"/>
            <span class="text-primary group-hover:text-brand text-caption transition-colors">پشتیبانی اختصاصی</span>
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
@reference "~/assets/styles/app.css";

.router-link-active span,
.router-link-active svg {
  @apply text-brand;
}

.router-link-active .active-badge {
  @apply bg-brand;
}


</style>